import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import { useState } from 'react';
import zeplinColor from '../../../../theme/Color';

type WidgetOption = {
  id: string;
  title: string;
  description: string;
  src?: string;
};

type AddWidgetModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd?: (widgetIds: string[]) => void;
  /** IDs of widgets that are currently enabled / already on the dashboard */
  enabledWidgets?: string[];
};

const widgetOptions: WidgetOption[] = [
  {
    id: 'actionable-accounts',
    title: 'Actionable Accounts',
    description: 'Re-engage quiet clients and accounts with the least recent interaction.',
    src: 'assets/account-card.svg',
  },
  {
    id: 'recent-activity',
    title: 'Activity',
    description: 'Track recent activities and interactions with your clients.',
    src: 'assets/activity-card.svg',
  },
  {
    id: 'tasks',
    title: 'Tasks',
    description: 'View and manage your upcoming tasks and to-dos.',
    src: 'assets/task-card.svg',
  },
  {
    id: 'schedule',
    title: "Today's Meetings",
    description: 'See all your scheduled meetings for the day at a glance.',
    src: 'assets/meeting-card.svg',
  },
  {
    id: 'deals-in-motion',
    title: 'Deals in Motion',
    description: 'Monitor active deals and their progress through the pipeline.',
    src: 'assets/deals-motion-card.svg',
  },
  {
    id: 'alerts',
    title: 'Alerts',
    description: 'Stay updated with important notifications and alerts.',
    src: 'assets/contact-card.svg',
  },
  {
    id: 'contacts',
    title: 'Contacts',
    description: 'View and manage your contacts and their details at a glance.',
    src: 'assets/contact-card.svg',
  },
];

const AddWidgetModal = ({ open, onClose, onAdd, enabledWidgets = [] }: AddWidgetModalProps) => {
  const [selected, setSelected] = useState<string[]>([]);

  const isAlreadyAdded = (id: string) => enabledWidgets.includes(id);

  const toggleSelect = (id: string) => {
    if (isAlreadyAdded(id)) return; // can't select already-added widgets
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleClose = () => {
    setSelected([]);
    onClose();
  };

  const handleAdd = () => {
    onAdd?.(selected);
    setSelected([]);
  };

  return (
    <CustomModal handleClose={handleClose} open={open}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CustomCard
          styles={{
            borderRadius: '14px',
            padding: { xs: '20px', xl: '24px' },
            backgroundColor: zeplinColor.Neutral1,
            width: '720px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: '4px',
            }}
          >
            <Typography variant='heading3' color={zeplinColor.Neutral11}>
              Add Widget
            </Typography>
            <IconButton onClick={handleClose} sx={{ p: '4px', mr: '-4px', mt: '-4px' }}>
              <CloseIcon sx={{ fontSize: 20, color: zeplinColor.Neutral8 }} />
            </IconButton>
          </Box>
          <Typography variant='body3' color={zeplinColor.Neutral8} sx={{ mb: '24px' }}>
            Choose the widgets you want to see when you start your day.
          </Typography>

          {/* Scrollable widget grid */}
          <Box
            sx={{
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {Array.from({ length: Math.ceil(widgetOptions.length / 3) }, (_, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                }}
              >
                {widgetOptions.slice(rowIndex * 3, rowIndex * 3 + 3).map((widget) => {
                  const added = isAlreadyAdded(widget.id);
                  const isSelected = selected.includes(widget.id);
                  return (
                    <Box
                      key={widget.id}
                      onClick={() => toggleSelect(widget.id)}
                      sx={{
                        borderRadius: '14px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        border: `1px solid ${zeplinColor.Neutral5}`,
                        backgroundColor: added
                          ? zeplinColor.Neutral2
                          : isSelected
                            ? '#F0F1FE'
                            : zeplinColor.Neutral2,
                        cursor: added ? 'default' : 'pointer',
                        opacity: added ? 0.6 : 1,
                        transition: 'all 0.15s ease',
                        ...(!added && {
                          '&:hover': {
                            borderColor: zeplinColor.BasicBlack,
                            backgroundColor: zeplinColor.Neutral5,
                          },
                        }),
                      }}
                    >
                      <img
                        src={widget.src}
                        alt={`${widget.title} icon`}
                        style={{
                          width: '100%',
                          height: '100%',
                          ...(added && { opacity: 0.5 }),
                        }}
                      />
                      <Typography
                        variant='responsiveBody3'
                        color={added ? zeplinColor.Neutral8 : zeplinColor.BasicBlack}
                        style={{ fontWeight: 505 }}
                      >
                        {widget.title}
                        {added && ' (Added)'}
                      </Typography>
                      <Typography
                        variant='body3'
                        color={added ? zeplinColor.Neutral8 : zeplinColor.Neutral8}
                      >
                        {widget.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          {/* Footer with Add button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '24px' }}>
            <CustomButton
              disabled={selected.length === 0}
              variant='contained'
              color='primary'
              style={{
                borderRadius: '10px',
                height: 36,
                minWidth: 'unset',
                px: '24px',
              }}
              text='Add'
              onClick={handleAdd}
              buttonVariant='buttonSmall'
            />
          </Box>
        </CustomCard>
      </Box>
    </CustomModal>
  );
};

export default AddWidgetModal;
